// src/app/App.tsx
import { Fragment, lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import Home from '@/pages/Home';
import Programs from '@/pages/Programs';
import Donate from '@/pages/Donate';
import LockerDonateRedirect from '@/pages/LockerDonateRedirect';
import Gallery from '@/pages/Gallery';
import Events from '@/pages/Events';
import ProgramDetail from '@/pages/ProgramDetail';
import NominateTeacher from '@/pages/NominateTeacher';
import NominateHub from '@/pages/NominateHub';
import NominateProspect from '@/pages/NominateProspect';
import ProspectNominationConfirm from '@/pages/ProspectNominationConfirm';
import Pool from '@/pages/Pool';
import NomineePortalLayout from '@/pages/portal/nominee/NomineePortalLayout';
import NomineePortalHome from '@/pages/portal/nominee/NomineePortalHome';
import NomineeRally from '@/pages/portal/nominee/NomineeRally';
import NomineeHistory from '@/pages/portal/nominee/NomineeHistory';
import NomineeMyLink from '@/pages/portal/nominee/NomineeMyLink';
import Ceremonies from '@/pages/public/Ceremonies';
import Support from '@/pages/Support';
import RallyPinboard from '@/pages/RallyPinboard';
import TeacherNominationConfirm from '@/pages/TeacherNominationConfirm';
import ProspectsList from '@/pages/prospects/ProspectsList';
import ProspectDetail from '@/pages/prospects/ProspectDetail';
import ProspectOnboard from '@/pages/prospect/ProspectOnboard';
import ProspectPortal from '@/pages/prospect/ProspectPortal';
import ProspectLanding from '@/pages/prospect/ProspectLanding';
import CommunityLanding from '@/pages/community/CommunityLanding';
import CommunitySponsorDetail from '@/pages/community/SponsorDetail';

import SponsorDashboard from '@/pages/SponsorDashboard';
import Login from '@/pages/Login';
import TeacherDashboard from '@/pages/TeacherDashboard';
import TeacherOverview from '@/pages/teacher/TeacherOverview';
import TeacherBudget from '@/pages/teacher/Budget';
import TeacherReimbursements from '@/pages/teacher/Reimbursements';
import TeacherSupport from '@/pages/teacher/Support';
import TeacherShopping from '@/pages/teacher/TeacherShopping';
import TeacherOrderStatus from '@/pages/teacher/TeacherOrderStatus';
import TeacherTools from '@/pages/teacher/TeacherTools';
import TeacherAITools from '@/pages/teacher/TeacherAITools';
import TeacherSettings from '@/pages/teacher/TeacherSettings';
import ClassroomSettings from '@/pages/teacher/ClassroomSettings';
import TeacherPublicClassroomPage from '@/pages/teacher/PublicClassroomPage';
import StorybooksHome from '@/pages/portal/teacher/storybooks/StorybooksHome';
import StorybooksCreate from '@/pages/portal/teacher/storybooks/StorybooksCreate';
import StorybooksDetail from '@/pages/portal/teacher/storybooks/StorybooksDetail';
import StorybooksSettings from '@/pages/portal/teacher/storybooks/StorybooksSettings';
import TeacherMarketplace from '@/pages/teacher/Marketplace';
import TeacherAiMarketplace from '@/pages/teacher/TeacherAiMarketplace';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminCopy from '@/pages/AdminCopy';
import AdminProvisionTeacher from '@/pages/admin/AdminProvisionTeacher';
import AdminBudgets from '@/pages/admin/Budgets';
import AdminReimbursements from '@/pages/admin/Reimbursements';
import AdminTickets from '@/pages/admin/Tickets';
import AdminSupportDashboard from '@/pages/admin/Support';
import AdminOpsDashboard from '@/pages/admin/OpsDashboard';
import AdminOrders from '@/pages/admin/Orders';
import ClassroomsDirectory from '@/pages/admin/ClassroomsDirectory';
import UsersDirectory from '@/pages/admin/UsersDirectory';
import Sponsors from '@/pages/admin/Sponsors';
import Nominations from '@/pages/admin/Nominations';
import Randomizer from '@/pages/admin/Randomizer';
import Analytics from '@/pages/admin/Analytics';
import AuditLog from '@/pages/admin/AuditLog';
import RoleHistory from '@/pages/admin/RoleHistory';
import Notifications from '@/pages/admin/Notifications';
import Allowlist, { AllowlistImport } from '@/pages/admin/Allowlist';
import CreateUser from '@/pages/admin/CreateUser';
import Broadcasts from '@/pages/admin/Broadcasts';
import AdminMarketplace from '@/pages/admin/Marketplace';
import AdminShop from '@/pages/admin/Shop';
import AdminSiteSettings from '@/pages/admin/Settings';
import BroadcastPlayer from '@/components/BroadcastPlayer';
import CreditPacks from '@/pages/admin/CreditPacks';
import SeasonalDecor from '@/pages/admin/SeasonalDecor';

import Mission from '@/pages/Mission';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import ContactThanks from '@/pages/ContactThanks';
import DonateSuccess from '@/pages/DonateSuccess';
import DonateCancel from '@/pages/DonateCancel';

import RequireAuth from '@/components/RequireAuth';
import RequireAdmin from '@/components/RequireAdmin';
import OverlayManager from '@/components/overlays/OverlayManager';
import { DISABLE_AUTH } from '@/lib/flags';
import { sponsorsEnabled } from '@/lib/feature-flags';
import type { RouteEntry } from '@/routeRegistry';
import { getRoutes, registerRoutes } from '@/routeRegistry';
import SiteHeader from '@/components/layout/SiteHeader';
import SiteFooter from '@/components/layout/SiteFooter';
import WelcomeModal from '@/components/WelcomeModal';
import AdminBar from '@/components/AdminBar';
import OfflineModeBanner from '@/components/OfflineModeBanner';
import SystemStatus from '@/components/SystemStatus';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AmbientMediaProvider } from '@/components/providers/AmbientMediaProvider';
import ClubPlayer from '@/components/social/ClubPlayer';

import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/supabase/client';

import AuthCallback from '@/pages/AuthCallback';
import SponsorSignup from '@/pages/SponsorSignup';
import SponsorRecent from '@/pages/SponsorRecent';
import Sponsor from '@/pages/Sponsor';
import MyGiving from '@/sponsor/MyGiving';
import SponsorWallet from '@/pages/sponsor/Wallet';
import DevTools from '@/pages/DevTools';

import SponsorClassroomAbout from '@/pages/programs/SponsorClassroomAbout';
import RentAssistanceAbout from '@/program/RentAssistanceAbout';
import TowerSMBsAbout from '@/program/TowerSMBsAbout';
import SponsorAthleteAbout from '@/program/SponsorAthleteAbout';

import Partners from '@/pages/Partners';
import PartnerApply from '@/pages/PartnerApply';
import ResetPassword from '@/pages/ResetPassword';
import SponsorAiLayout from '@/pages/portal/sponsor/SponsorAiLayout';
import SponsorAiTools from '@/pages/portal/sponsor/SponsorAiTools';
import SponsorAiMarketplace from '@/pages/portal/sponsor/SponsorAiMarketplace';
import SponsorAiApp from '@/pages/portal/sponsor/SponsorAiApp';
import LockerRoomLayout from '@/pages/portal/locker/LockerRoomLayout';
import LockerRoomHome from '@/pages/portal/locker/LockerRoomHome';
import LockerRoomLeaderboard from '@/pages/portal/locker/Leaderboard';
import CoachAiTools from '@/pages/portal/coach/AiTools';
import CoachAiMarketplace from '@/pages/portal/coach/CoachAiMarketplace';
import CoachMarketplace from '@/pages/portal/coach/Marketplace';
import LockerRoomApp from '@/pages/portal/locker/LockerRoomApp';
import LockerRoomBudget from '@/pages/portal/locker/LockerRoomBudget';
import LockerRoomShopping from '@/pages/portal/locker/LockerRoomShopping';
import ProspectMarketplace from '@/pages/portal/prospect/ProspectMarketplace';
import ProspectProfile from '@/pages/portal/prospect/ProspectProfile';
import ProspectAiTools from '@/pages/portal/prospect/AiTools';
import ProspectAiMarketplace from '@/pages/portal/prospect/ProspectAiMarketplace';
import LockerPublic from '@/pages/locker/LockerPublic';
import TeacherShare from '@/pages/public/TeacherShare';
import ClassroomProfile from '@/pages/public/ClassroomProfile';
import SponsorProfilePage from '@/pages/public/SponsorProfile';
import UserProfilePage from '@/pages/public/UserProfile';
import MyProfile from '@/pages/portal/MyProfile';
import LockerRoomPublicPage from '@/pages/portal/locker/LockerRoomPublicPage';
import LockerRoomNominees from '@/pages/portal/locker/LockerRoomNominees';
import LockerRoomRoster from '@/pages/portal/locker/LockerRoomRoster';
import ParentHome, { ParentHomeOverview } from '@/pages/portal/parent/ParentHome';
import ParentSupport from '@/pages/portal/parent/ParentSupport';
import ParentStudentDetail from '@/pages/portal/parent/ParentStudentDetail';
import ParentWallet from '@/pages/portal/parent/ParentWallet';
import ParentMarketplace from '@/pages/portal/parent/ParentMarketplace';
import StudentProfileBuilder from '@/pages/portal/parent/StudentProfileBuilder';
import GuardianInvite from '@/pages/invite/GuardianInvite';
import GuardianHome from '@/pages/portal/guardian/GuardianHome';
import GuardianMessagesInbox from '@/pages/portal/guardian/GuardianMessagesInbox';
import GuardianMessageThread from '@/pages/portal/guardian/GuardianMessageThread';
import NewMessage from '@/pages/portal/guardian/NewMessage';
import GuardianAiTools from '@/pages/portal/guardian/GuardianAiTools';
import GuardianAiMarketplace from '@/pages/portal/guardian/GuardianAiMarketplace';
import CoachWallet from '@/pages/portal/coach/CoachWallet';
import KineticReview from '@/pages/portal/coach/KineticReview';
import DailyRoutine from '@/pages/portal/prospect/DailyRoutine';
import SponsorClub from '@/pages/portal/sponsor/Club';
import TeacherCorner from '@/pages/portal/teacher/Corner';
import TheCombine from '@/pages/portal/prospect/Combine';
import TrackerPage from '@/pages/portal/smartblocks/TrackerPage';
import TeacherStudentDetail from '@/pages/portal/teacher/StudentDetail';
import StudentHome from '@/pages/portal/student/StudentHome';
import StudentAiTools from '@/pages/portal/student/StudentAiTools';
import StudentAiMarketplace from '@/pages/portal/student/StudentAiMarketplace';
import TeacherMessagesInbox from '@/pages/portal/teacher/TeacherMessagesInbox';
import TeacherMessageThread from '@/pages/portal/teacher/TeacherMessageThread';

type GlobalNotice = {
  id: string | number;
  title: string;
  message: string;
};

const enableNumaAssistant = import.meta.env?.VITE_ENABLE_NUMA === 'true';
const NumaAssistantWidget = enableNumaAssistant ? lazy(() => import('@/components/NumaAssistant')) : null;

const BASE_ROUTE_ENTRIES = [
  { path: '/', label: 'Home' },
  { path: '/programs', label: 'Programs' },
  { path: '/gallery', label: 'Gallery' },
  { path: '/events', label: 'Events' },
  { path: '/partners', label: 'Partners' },
  { path: '/contact', label: 'Contact' },
  { path: '/contact/thanks', label: 'Contact Thanks' },
  { path: '/nominate', label: 'Nominate' },
  { path: '/nominate/teacher', label: 'Nominate a Teacher' },
  { path: '/nominate/prospect', label: 'Nominate a Prospect' },
  { path: '/nominate-teacher', label: 'Nominate a Teacher' },
  { path: '/rally', label: 'Rally Board' },
  { path: '/teachers/nomination-confirm', label: 'Confirm Teacher Nomination' },
  { path: '/prospects', label: 'Prospects' },
  { path: '/prospects/:id', label: 'Prospect Detail' },
  { path: '/prospect', label: 'Prospect Landing' },
  { path: '/prospect/:handle', label: 'Prospect Portal' },
  { path: '/prospect/onboard', label: 'Prospect Onboard' },
  { path: '/community', label: 'Community Sponsors' },
  { path: '/community/s/:handle', label: 'Community Sponsor Profile' },
  { path: '/portal/prospect', label: 'Locker Room' },
  { path: '/portal/prospect/tools', label: 'Locker Room Tools' },
  { path: '/portal/prospect/app', label: 'Locker Room App' },
  { path: '/portal/sponsor', label: 'Sponsor Prospect Portal' },
  { path: '/sponsor/ai', label: 'Sponsor AI Studio' },
  { path: '/sponsor/ai/tools', label: 'Sponsor AI Tools' },
  { path: '/sponsor/ai/app', label: 'Sponsor AI App' },
  { path: '/s/:handle', label: 'Sponsor Profile' },
  { path: '/t/:handle', label: 'Teacher Profile' },
  { path: '/coach/:handle', label: 'Coach Profile' },
  { path: '/a/:handle', label: 'Athlete Profile' },
  { path: '/u/:handle', label: 'User Profile' },
  { path: '/portal/teacher', label: 'Teacher Dashboard' },
  { path: '/portal/teacher/budget', label: 'Teacher Budget' },
  { path: '/portal/teacher/reimbursements', label: 'Teacher Reimbursements' },
  { path: '/portal/teacher/support', label: 'Teacher Support' },
  { path: '/portal/teacher/shopping', label: 'Teacher Shopping' },
  { path: '/portal/teacher/marketplace', label: 'Teacher Marketplace' },
  { path: '/portal/teacher/public', label: 'Teacher Public Classroom' },
  { path: '/portal/teacher/messages', label: 'Teacher Messages' },
  { path: '/portal/teacher/messages/thread/:threadId', label: 'Teacher Message Thread' },
  { path: '/portal/teacher/messages/new', label: 'New Teacher Message Thread' },
  { path: '/portal/teacher/order/:id', label: 'Teacher Order Status' },
  { path: '/portal/teacher/ai-tools', label: 'Teacher AI Tools' },
  { path: '/portal/teacher/classroom-settings', label: 'Classroom Settings' },
  { path: '/portal/teacher/classroom/:id/settings', label: 'Classroom Settings' },
  { path: '/portal/parent/marketplace', label: 'Parent Marketplace' },
  { path: '/portal/guardian', label: 'Guardian Portal' },
  { path: '/portal/guardian/home', label: 'Guardian Home' },
  { path: '/portal/guardian/messages', label: 'Guardian Messages' },
  { path: '/portal/guardian/messages/new', label: 'New Guardian Message Thread' },
  { path: '/portal/guardian/messages/thread/:threadId', label: 'Guardian Message Thread' },
  { path: '/portal/teacher/storybooks', label: 'Teacher Storybooks' },
  { path: '/portal/teacher/storybooks/new', label: 'Create Storybook' },
  { path: '/portal/teacher/storybooks/:id', label: 'Storybook Detail' },
  { path: '/portal/teacher/storybooks/settings', label: 'Storybook Settings' },
  { path: '/portal/sponsor/club', label: 'Sponsors Club' },
  { path: '/portal/teacher/corner', label: 'Teachers Corner' },
  { path: '/portal/prospect/combine', label: 'The Combine' },
  { path: '/portal/coach/combine', label: 'The Combine (Coach)' },
  { path: '/portal/coach/wallet', label: 'Coach Wallet' },
  { path: '/portal/parent', label: 'Parent Portal' },
  { path: '/portal/parent/support', label: 'Parent Portal Support' },
  { path: '/portal/parent/wallet', label: 'Parent Wallet' },
  { path: '/portal/parent/students/:studentId', label: 'Parent Student Detail' },
  { path: '/portal/student', label: 'Student Portal' },
  { path: '/admin', label: 'Admin Overview' },
  { path: '/admin/copy', label: 'Admin Copy' },
  { path: '/portal/admin', label: 'Admin Dashboard' },
  { path: '/admin/classrooms', label: 'Admin Classrooms Directory' },
  { path: '/admin/users', label: 'Admin Users Directory' },
  { path: '/admin/create-user', label: 'Admin Create User' },
  { path: '/admin/sponsors', label: 'Admin Sponsor Requests' },
  { path: '/admin/nominations', label: 'Admin Teacher Nominations' },
  { path: '/admin/randomizer', label: 'Admin Randomizer' },
  { path: '/admin/broadcasts', label: 'Admin Broadcasts' },
  { path: '/portal/admin/broadcasts', label: 'Admin Broadcasts' },
  { path: '/admin/analytics', label: 'Admin Analytics' },
  { path: '/admin/audit', label: 'Admin Audit Log' },
  { path: '/admin/budgets', label: 'Admin Budgets' },
  { path: '/admin/orders', label: 'Admin Orders' },
  { path: '/admin/reimbursements', label: 'Admin Reimbursements' },
  { path: '/admin/support', label: 'Admin Support Dashboard' },
  { path: '/admin/tickets', label: 'Admin Tickets' },
  { path: '/admin/ops-dashboard', label: 'Admin Ops Dashboard' },
  { path: '/admin/role-history', label: 'Admin Role History' },
  { path: '/admin/packs', label: 'Admin Credit Packs' },
  { path: '/admin/seasonal', label: 'Admin Seasonal Decor' },
  { path: '/admin/allowlist', label: 'Admin Allowlist' },
  { path: '/admin/allowlist/import', label: 'Admin Allowlist Import' },
  { path: '/admin/provision-teacher', label: 'Provision Teacher' },
  { path: '/admin/settings', label: 'Admin Site Settings' },
  { path: '/auth/callback', label: 'Auth Callback' },
  { path: '/reset-password', label: 'Reset Password' },
  { path: '/donate/success', label: 'Donate Success' },
  { path: '/donate/cancel', label: 'Donate Cancel' },
  { path: '/login', label: 'Login' },
  { path: '/mission', label: 'Mission' },
  { path: '/about', label: 'About' },
  { path: '/programs/sponsor-classroom/about', label: 'Sponsor Classroom About' },
  { path: '/programs/rent-assistance/about', label: 'Rent Assistance About' },
  { path: '/programs/tower-smbs/about', label: 'Tower SMBs About' },
  { path: '/programs/sponsor-athlete/about', label: 'Sponsor Athlete About' },
  { path: '/programs/:programId', label: 'Program Detail' },
  { path: '/:programId/donate', label: 'Program Donate' },
  { path: '/partners/apply', label: 'Partner Apply' },
  { path: '/t/:code', label: 'Teacher Share' },
  { path: '/locker/:slug', label: 'Locker Room' },
  { path: '/dev', label: 'Dev Tools' },
];

const SPONSOR_ROUTE_ENTRIES = [
  { path: '/sponsor', label: 'Sponsor' },
  { path: '/sponsor/signup', label: 'Sponsor Signup' },
  { path: '/sponsor/recent', label: 'Sponsor Recent Highlights' },
  { path: '/sponsor/dashboard', label: 'Sponsor Dashboard' },
  { path: '/sponsor/giving', label: 'Sponsor Giving' },
  { path: '/sponsor/wallet', label: 'Sponsor Wallet' },
];

function RedirectLegacyTeacherDashboard() {
  const location = useLocation();
  const nextPath = location.pathname.replace(/^\/teacher\/dashboard/, '/portal/teacher');
  return (
    <Navigate
      to={{
        pathname: nextPath,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function RedirectLegacyAdminDashboard() {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/portal/admin',
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

export default function App() {
  const { pathname } = useLocation();
  const sponsorFeature = sponsorsEnabled();
  const isSponsorDashboard =
    pathname.startsWith('/sponsor/dashboard') || pathname.startsWith('/portal/sponsor');
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin' || profile?.role === 'superadmin';
  const [globalNotice, setGlobalNotice] = useState<GlobalNotice | null>(null);
  const [registeredRoutes, setRegisteredRoutes] = useState<RouteEntry[]>(() => getRoutes());

  useEffect(() => {
    const routesToRegister = [
      ...BASE_ROUTE_ENTRIES,
      ...(sponsorsEnabled() ? SPONSOR_ROUTE_ENTRIES : []),
    ];

    registerRoutes(routesToRegister);
    setRegisteredRoutes(getRoutes());
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const channel = supabase
      .channel('admin-notification-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'ledger', table: 'admin_notifications' }, (payload) => {
        const data = payload?.new;
        if (data) {
          setGlobalNotice({
            id: data.id,
            title: data.title || 'New admin notification',
            message: data.message || data.body || 'A new notification is available.',
          });
        }
      });
    channel.subscribe();
    return () => {
      channel.unsubscribe();
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!globalNotice) return undefined;
    const timer = setTimeout(() => setGlobalNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [globalNotice]);
  return (
    <AmbientMediaProvider>
    <div className="min-h-screen bg-sand text-cocoa flex flex-col">
      <OverlayManager />
      <AdminBar />
      <SiteHeader />
      <OfflineModeBanner />
      <SystemStatus />

      {isAdmin && globalNotice ? (
        <div className="mx-auto my-3 w-full max-w-4xl rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <strong className="font-semibold">{globalNotice.title}</strong>
              <p className="mt-1 text-sm text-emerald-800">{globalNotice.message}</p>
            </div>
            <button
              type="button"
              className="text-xs font-semibold uppercase tracking-wide text-emerald-700 hover:text-emerald-900"
              onClick={() => setGlobalNotice(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {DISABLE_AUTH && (
        <div className="mx-auto my-3 w-full max-w-6xl rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
          <strong>Dev Nav (auth disabled):</strong>{' '}
          {sponsorFeature && (
            <>
              <a href="/sponsor/dashboard" className="mr-3 underline">Sponsor Dashboard</a>
              <a href="/portal/sponsor" className="mr-3 underline">Sponsor Portal</a>
            </>
          )}
          <a href="/portal/teacher" className="mr-3 underline">Teacher Dashboard</a>
          <a href="/portal/admin" className="mr-3 underline">Admin Dashboard</a>
          <a href="/portal/prospect" className="mr-3 underline">Prospect Portal</a>
          <a href="/contact/thanks" className="mr-3 underline">Contact Thanks</a>
          <a href="/auth/callback?debug=1" className="mr-3 underline">Auth Callback</a>
          {sponsorFeature && (
            <>
              <a href="/sponsor/signup" className="mr-3 underline">Sponsor Signup</a>
              <a href="/sponsor/recent" className="mr-3 underline">Sponsor Recent</a>
            </>
          )}
          <a href="/dev" className="mr-3 underline">Dev Tools</a>
        </div>
      )}

      <main className={isSponsorDashboard ? 'flex-1' : 'max-w-6xl mx-auto p-4 w-full flex-1'}>
        <Fragment key={registeredRoutes.length}>
          <Routes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/programs/sponsor-classroom/about" element={<SponsorClassroomAbout />} />
          <Route path="/programs/rent-assistance/about" element={<RentAssistanceAbout />} />
          <Route path="/programs/tower-smbs/about" element={<TowerSMBsAbout />} />
          <Route path="/programs/sponsor-athlete/about" element={<SponsorAthleteAbout />} />
          <Route path="/:programId/donate" element={<Donate />} />
          <Route path="/donate/locker" element={<LockerDonateRedirect />} />
          <Route path="/donate/locker/:code" element={<LockerDonateRedirect />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/events" element={<Events />} />
          <Route path="/programs/:programId" element={<ProgramDetail />} />
          <Route path="/donate/success" element={<DonateSuccess />} />
          <Route path="/donate/cancel" element={<DonateCancel />} />
          <Route path="/mission" element={<Mission />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/contact/thanks" element={<ContactThanks />} />
          <Route path="/nominate" element={<NominateHub />} />
          <Route path="/nominate/teacher" element={<NominateTeacher />} />
          <Route path="/nominate/prospect" element={<NominateProspect />} />
          <Route path="/nominate-teacher" element={<NominateTeacher />} />
          <Route path="/support/:code" element={<Support />} />
          <Route path="/rally" element={<RallyPinboard />} />
          <Route path="/teachers/nomination-confirm" element={<TeacherNominationConfirm />} />
          <Route path="/nominations/confirm-prospect" element={<ProspectNominationConfirm />} />
          <Route path="/pool" element={<Pool />} />
          <Route path="/ceremonies" element={<Ceremonies />} />
          <Route
            path="/portal/nominee"
            element={
              <RequireAuth allowedRoles={['nominee', 'teacher', 'prospect', 'athlete', 'coach', 'admin', 'superadmin']}>
                <NomineePortalLayout />
              </RequireAuth>
            }
          >
            <Route index element={<NomineePortalHome />} />
            <Route path="rally" element={<NomineeRally />} />
            <Route path="history" element={<NomineeHistory />} />
            <Route path="support" element={<NomineeMyLink />} />
          </Route>
          <Route path="/invite/guardian" element={<GuardianInvite />} />
          <Route path="/t/:code" element={<TeacherShare />} />
          <Route path="/c/:classroomId" element={<ClassroomProfile />} />
          <Route path="/locker/:slug" element={<LockerPublic />} />
          <Route path="/prospects" element={<ProspectsList />} />
          <Route path="/prospects/:id" element={<ProspectDetail />} />
          <Route path="/prospect" element={<ProspectLanding />} />
          <Route path="/prospect/:handle" element={<ProspectPortal />} />
          <Route path="/community" element={<CommunityLanding />} />
          <Route path="/community/s/:handle" element={<CommunitySponsorDetail />} />
          {sponsorFeature && <Route path="/sponsor" element={<Sponsor />} />}
          {sponsorFeature && <Route path="/sponsor/signup" element={<SponsorSignup />} />}
          {sponsorFeature && <Route path="/sponsor/recent" element={<SponsorRecent />} />}

          {sponsorFeature && (
            <Route
              path="/sponsor/portal"
              element={
                <RequireAuth role="sponsor">
                  <Navigate to="/sponsor/dashboard" replace />
                </RequireAuth>
              }
            />
          )}

          <Route path="/partners" element={<Partners />} />
          <Route path="/partners/apply" element={<PartnerApply />} />
          <Route path="/dev" element={<DevTools />} />

          <Route
            path="/prospect/onboard"
            element={
              <RequireAuth allowedRoles={['coach', 'admin']}>
                <ProspectOnboard />
              </RequireAuth>
            }
          />

          {/* Auth-related public endpoints */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/login" element={<Login />} />

          {/* Protected (sponsor) */}
          {sponsorFeature && (
            <Route
              path="/sponsor/giving"
              element={
                <RequireAuth role="sponsor">
                  <MyGiving />
                </RequireAuth>
              }
            />
          )}
          {sponsorFeature && (
            <Route
              path="/sponsor/wallet"
              element={
                <RequireAuth role="sponsor">
                  <SponsorWallet />
                </RequireAuth>
              }
            />
          )}
          {sponsorFeature && (
            <Route
              path="/portal/sponsor/credits"
              element={
                <RequireAuth role="sponsor">
                  <Navigate to="/sponsor/wallet" replace />
                </RequireAuth>
              }
            />
          )}
          {sponsorFeature && (
            <Route
              path="/portal/sponsor"
              element={
                <RequireAuth role="sponsor">
                  <Navigate to="/sponsor/dashboard" replace />
                </RequireAuth>
              }
            />
          )}
          {sponsorFeature && (
            <Route
              path="/portal/sponsor/*"
              element={
                <RequireAuth role="sponsor">
                  <Navigate to="/sponsor/dashboard" replace />
                </RequireAuth>
              }
            />
          )}
          {sponsorFeature && (
            <Route
              path="/sponsor/dashboard/*"
              element={
                <RequireAuth role="sponsor">
                  <SponsorDashboard />
                </RequireAuth>
              }
            />
          )}
          {sponsorFeature && (
            <Route
              path="/sponsor/ai/*"
              element={
                <RequireAuth allowedRoles={['sponsor', 'admin', 'superadmin']}>
                  <SponsorAiLayout />
                </RequireAuth>
              }
            >
              <Route index element={<SponsorAiTools />} />
              <Route path="tools" element={<SponsorAiTools />} />
              <Route path="marketplace" element={<SponsorAiMarketplace />} />
              <Route path="app" element={<SponsorAiApp />} />
            </Route>
          )}

          {/* Sponsors Club */}
          <Route
            path="/portal/sponsor/club"
            element={
              <RequireAuth allowedRoles={['sponsor', 'admin', 'superadmin']}>
                <SponsorClub />
              </RequireAuth>
            }
          />

          {/* Protected (teacher) */}
          <Route
            path="/portal/teacher/*"
            element={
              <RequireAuth allowedRoles={['teacher', 'nominee', 'admin', 'superadmin']}>
                <TeacherDashboard />
              </RequireAuth>
            }
          >
            <Route index element={<TeacherOverview />} />
            <Route path="budget" element={<TeacherBudget />} />
            <Route path="reimbursements" element={<TeacherReimbursements />} />
            <Route path="support" element={<TeacherSupport />} />
            <Route path="shopping" element={<TeacherShopping />} />
            <Route path="marketplace" element={<TeacherMarketplace />} />
            <Route path="ai-marketplace" element={<TeacherAiMarketplace />} />
            <Route path="public" element={<TeacherPublicClassroomPage />} />
            <Route path="messages" element={<TeacherMessagesInbox />} />
            <Route path="messages/thread/:threadId" element={<TeacherMessageThread />} />
            <Route
              path="messages/new"
              element={<Navigate to="/portal/teacher/messages" replace />}
            />
            <Route path="order/:id" element={<TeacherOrderStatus />} />
            <Route path="tools" element={<TeacherTools />} />
            <Route path="ai-tools" element={<TeacherAITools />} />
            <Route path="students/:studentId" element={<TeacherStudentDetail />} />
            <Route path="settings" element={<TeacherSettings />} />
            <Route path="classroom-settings" element={<ClassroomSettings />} />
            <Route path="classroom/:id/settings" element={<ClassroomSettings />} />
            <Route path="storybooks">
              <Route index element={<ErrorBoundary><StorybooksHome /></ErrorBoundary>} />
              <Route path="new" element={<ErrorBoundary><StorybooksCreate /></ErrorBoundary>} />
              <Route path=":id" element={<ErrorBoundary><StorybooksDetail /></ErrorBoundary>} />
              <Route path="settings" element={<ErrorBoundary><StorybooksSettings /></ErrorBoundary>} />
            </Route>
            <Route path="corner" element={<TeacherCorner />} />
          </Route>

          {/* Convenience alias (legacy) */}
          <Route path="/teacher/dashboard/*" element={<RedirectLegacyTeacherDashboard />} />

          <Route
            path="/teacher/storybooks"
            element={
              <RequireAuth allowedRoles={['teacher', 'nominee', 'admin', 'superadmin']}>
                <Navigate to="/portal/teacher/storybooks" replace />
              </RequireAuth>
            }
          />
          <Route
            path="/teacher/storybooks/new"
            element={
              <RequireAuth allowedRoles={['teacher', 'nominee', 'admin', 'superadmin']}>
                <Navigate to="/portal/teacher/storybooks/new" replace />
              </RequireAuth>
            }
          />

          <Route
            path="/portal/coach/wallet"
            element={
              <RequireAuth allowedRoles={['coach', 'admin', 'superadmin']}>
                <ErrorBoundary fallbackPath="/portal/coach/wallet" fallbackLabel="Back to Wallet">
                  <CoachWallet />
                </ErrorBoundary>
              </RequireAuth>
            }
          />
          <Route
            path="/portal/coach/marketplace"
            element={
              <RequireAuth allowedRoles={['coach', 'admin', 'superadmin']}>
                <ErrorBoundary fallbackPath="/portal/coach/marketplace" fallbackLabel="Back to Marketplace">
                  <CoachMarketplace />
                </ErrorBoundary>
              </RequireAuth>
            }
          />
          <Route
            path="/portal/coach/tools"
            element={
              <RequireAuth allowedRoles={['coach', 'admin', 'superadmin']}>
                <ErrorBoundary fallbackPath="/portal/coach/tools" fallbackLabel="Back to Tools">
                  <CoachAiTools />
                </ErrorBoundary>
              </RequireAuth>
            }
          />
          <Route
            path="/portal/coach/ai-marketplace"
            element={
              <RequireAuth allowedRoles={['coach', 'admin', 'superadmin']}>
                <ErrorBoundary fallbackPath="/portal/coach/ai-marketplace" fallbackLabel="Back to AI Marketplace">
                  <CoachAiMarketplace />
                </ErrorBoundary>
              </RequireAuth>
            }
          />
          <Route
            path="/portal/coach/review"
            element={
              <RequireAuth allowedRoles={['coach', 'admin', 'superadmin']}>
                <ErrorBoundary fallbackPath="/portal/coach/review" fallbackLabel="Back to Review">
                  <KineticReview />
                </ErrorBoundary>
              </RequireAuth>
            }
          />
          <Route
            path="/portal/coach/combine"
            element={
              <RequireAuth allowedRoles={['coach', 'admin', 'superadmin']}>
                <TheCombine />
              </RequireAuth>
            }
          />

          <Route
            path="/portal/parent/*"
            element={
              <RequireAuth allowedRoles={['guardian']}>
                <ParentHome />
              </RequireAuth>
            }
          >
            <Route index element={<ParentHomeOverview />} />
            <Route path="support" element={<ParentSupport />} />
            <Route path="marketplace" element={<ParentMarketplace />} />
            <Route path="messages" element={<GuardianMessagesInbox />} />
            <Route path="messages/new" element={<GuardianMessageThread />} />
            <Route path="messages/thread/:threadId" element={<GuardianMessageThread />} />
          </Route>
          <Route
            path="/portal/parent/wallet"
            element={
              <RequireAuth allowedRoles={['guardian']}>
                <ParentWallet />
              </RequireAuth>
            }
          />
          <Route
            path="/portal/parent/students/:studentId"
            element={
              <RequireAuth allowedRoles={['guardian']}>
                <ParentStudentDetail />
              </RequireAuth>
            }
          />
          <Route
            path="/portal/parent/students/:studentId/profile"
            element={
              <RequireAuth allowedRoles={['guardian']}>
                <StudentProfileBuilder />
              </RequireAuth>
            }
          />
          {/* Redirect /portal/guardian → /portal/parent */}
          <Route path="/portal/guardian" element={<Navigate to="/portal/parent" replace />} />
          <Route path="/portal/guardian/home" element={<Navigate to="/portal/parent" replace />} />
          <Route path="/portal/guardian/messages" element={<Navigate to="/portal/parent/messages" replace />} />
          <Route path="/portal/guardian/messages/*" element={<Navigate to="/portal/parent/messages" replace />} />
          <Route
            path="/portal/guardian/tools"
            element={
              <RequireAuth allowedRoles={['guardian']}>
                <GuardianAiTools />
              </RequireAuth>
            }
          />
          <Route
            path="/portal/guardian/ai-marketplace"
            element={
              <RequireAuth allowedRoles={['guardian']}>
                <GuardianAiMarketplace />
              </RequireAuth>
            }
          />
          <Route
            path="/portal/student"
            element={
              <RequireAuth allowedRoles={['student', 'prospect', 'athlete']}>
                <StudentHome />
              </RequireAuth>
            }
          />
          <Route
            path="/portal/student/tools"
            element={
              <RequireAuth allowedRoles={['student', 'prospect', 'athlete']}>
                <StudentAiTools />
              </RequireAuth>
            }
          />
          <Route
            path="/portal/student/ai-marketplace"
            element={
              <RequireAuth allowedRoles={['student', 'prospect', 'athlete']}>
                <StudentAiMarketplace />
              </RequireAuth>
            }
          />

          {/* Protected (admin) */}
          <Route
            path="/portal/admin"
            element={
              <RequireAuth role="admin">
                <AdminDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <Navigate to="/portal/admin" replace />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/copy"
            element={
              <RequireAdmin>
                <AdminCopy />
              </RequireAdmin>
            }
          />

        <Route
          path="/admin/classrooms"
          element={
            <RequireAuth allowedRoles={['admin', 'superadmin']}>
              <ClassroomsDirectory />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireAuth allowedRoles={['admin', 'superadmin']}>
              <UsersDirectory />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/create-user"
          element={
            <RequireAuth allowedRoles={['admin', 'superadmin']}>
              <CreateUser />
            </RequireAuth>
          }
        />
          <Route
            path="/admin/sponsors"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <Sponsors />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/nominations"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <Nominations />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/randomizer"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <Randomizer />
              </RequireAuth>
            }
          />
          <Route
            path="/portal/admin/broadcasts"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <Broadcasts />
              </RequireAuth>
            }
          />
          <Route
            path="/portal/admin/marketplace"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminMarketplace />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/broadcasts"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <Broadcasts />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/marketplace"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminMarketplace />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/shop"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminShop />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <Notifications />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/allowlist"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <Allowlist />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/allowlist/import"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AllowlistImport />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <Analytics />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/budgets"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminBudgets />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/packs"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <CreditPacks />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/seasonal"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <SeasonalDecor />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AuditLog />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminOrders />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/reimbursements"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminReimbursements />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/support"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminSupportDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/tickets"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminTickets />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/ops-dashboard"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminOpsDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/role-history"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <RoleHistory />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminSiteSettings />
              </RequireAuth>
            }
          />

          <Route path="/broadcasts" element={<BroadcastPlayer />} />

          <Route
            path="/admin/provision-teacher"
            element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <AdminProvisionTeacher />
              </RequireAuth>
            }
          />

          <Route
            path="/portal/prospect/*"
            element={
              <RequireAuth allowedRoles={['nominee', 'athlete', 'guardian', 'prospect', 'coach', 'admin', 'superadmin']}>
                <ErrorBoundary fallbackPath="/portal/prospect" fallbackLabel="Back to Locker Room">
                  <LockerRoomLayout />
                </ErrorBoundary>
              </RequireAuth>
            }
          >
            <Route index element={<LockerRoomHome />} />
            <Route path="profile" element={<ProspectProfile />} />
            <Route path="budget" element={<LockerRoomBudget />} />
            <Route path="shopping" element={<LockerRoomShopping />} />
            <Route path="marketplace" element={<ProspectMarketplace />} />
            <Route path="tools" element={<ProspectAiTools />} />
            <Route path="ai-marketplace" element={<ProspectAiMarketplace />} />
            <Route path="tracker" element={<TrackerPage backPath="/portal/prospect/tools" role="prospect" />} />
            <Route path="app" element={<LockerRoomApp />} />
            <Route path="routine" element={<DailyRoutine />} />
            <Route path="combine" element={<TheCombine />} />
          </Route>

          {/* Locker Room Portal - Dual view for coaches and prospects */}
          <Route
            path="/portal/locker/*"
            element={
              <RequireAuth allowedRoles={['nominee', 'athlete', 'guardian', 'prospect', 'coach', 'admin', 'superadmin']}>
                <ErrorBoundary fallbackPath="/portal/locker" fallbackLabel="Back to Locker Room">
                  <LockerRoomLayout />
                </ErrorBoundary>
              </RequireAuth>
            }
          >
            <Route index element={<LockerRoomHome />} />
            <Route path="budget" element={<LockerRoomBudget />} />
            <Route path="shopping" element={<LockerRoomShopping />} />
            <Route path="marketplace" element={<CoachMarketplace />} />
            <Route path="leaderboard" element={<LockerRoomLeaderboard />} />
            <Route path="tools" element={<CoachAiTools />} />
            <Route path="ai-marketplace" element={<CoachAiMarketplace />} />
            <Route path="tracker" element={<TrackerPage backPath="/portal/locker/tools" role="coach" />} />
            <Route path="app" element={<LockerRoomApp />} />
            <Route path="public" element={<LockerRoomPublicPage />} />
            <Route path="nominees" element={<LockerRoomNominees />} />
            <Route path="roster" element={<LockerRoomRoster />} />
          </Route>

          {/* Admin legacy aliases */}
          <Route path="/admin/dashboard" element={<RedirectLegacyAdminDashboard />} />

          <Route path="/s/:handle" element={<SponsorProfilePage />} />
          <Route path="/sponsor/:handle" element={<SponsorProfilePage />} />

          {/* Universal user profiles */}
          <Route path="/t/:handle" element={<UserProfilePage />} />
          <Route path="/coach/:handle" element={<UserProfilePage />} />
          <Route path="/a/:handle" element={<UserProfilePage />} />
          <Route path="/u/:handle" element={<UserProfilePage />} />

          {/* Fallback */}
          <Route path="*" element={<Home />} />
          </Routes>
        </Fragment>
      </main>

      <ClubPlayer />
      <SiteFooter />
      <WelcomeModal />
      {enableNumaAssistant && NumaAssistantWidget ? (
        <Suspense fallback={null}>
          <NumaAssistantWidget />
        </Suspense>
      ) : null}
    </div>
    </AmbientMediaProvider>
  );
}
